import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, CheckCircle2, AlertTriangle, Loader2, Pen, 
  Upload, Shield, Download, Building2, User, Calendar,
  FileCheck, Clock, X, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/logos/sterilemate-white.png?v=4";

export default function ContractorAgreementPage() {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState(null);
  
  // Signature state
  const [signatureType, setSignatureType] = useState("typed"); // "typed" or "drawn"
  const [typedSignature, setTypedSignature] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({});
  
  // Canvas ref for drawn signatures
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchAgreement();
  }, [agreementId]);

  const fetchAgreement = async () => {
    try {
      const res = await fetch(`${API}/contractor-compliance/agreement/${agreementId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Agreement not found");
      }
      const data = await res.json();
      setAgreement(data);
      setTemplate(data.template);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionNum) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionNum]: !prev[sectionNum]
    }));
  };

  const expandAllSections = () => {
    const allExpanded = {};
    template?.sections?.forEach(s => {
      allExpanded[s.number] = true;
    });
    setExpandedSections(allExpanded);
  };

  const collapseAllSections = () => {
    setExpandedSections({});
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    ctx.lineTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getSignatureData = () => {
    if (signatureType === "typed") {
      return typedSignature;
    } else {
      const canvas = canvasRef.current;
      return canvas.toDataURL("image/png");
    }
  };

  const handleSubmit = async () => {
    if (!acceptedTerms) {
      toast.error("Please accept the terms to continue");
      return;
    }

    const signatureData = getSignatureData();
    if (!signatureData || (signatureType === "typed" && !typedSignature.trim())) {
      toast.error("Please provide your signature");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contractor-compliance/sign-agreement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agreement_id: agreementId,
          signature_data: signatureData,
          signature_type: signatureType,
          signer_name: signatureType === "typed" ? typedSignature : agreement.contractor_name,
          signer_title: signerTitle || null
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to sign agreement");
      }

      const signResult = await res.json();
      toast.success("Agreement signed successfully!");
      
      // Trigger PDF generation and email
      try {
        const emailRes = await fetch(`${API}/contractor-compliance/agreement/${agreementId}/email?recipient_email=${encodeURIComponent(agreement.contractor_email)}`, {
          method: "POST"
        });
        if (emailRes.ok) {
          toast.success("Signed agreement PDF has been emailed to you!");
        }
      } catch (emailErr) {
        console.log("Email sending failed:", emailErr);
        // Don't block navigation if email fails
      }
      
      // Navigate to compliance documents upload
      navigate(`/contractor/compliance/${agreement.contractor_id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Agreement Not Found</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/contractor/dashboard")} variant="outline" className="border-slate-600 text-slate-300">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (agreement?.status === "signed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-emerald-500/30 rounded-2xl p-8 max-w-md text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Agreement Already Signed</h2>
          <p className="text-slate-400 mb-2">Signed on: {new Date(agreement.signed_at).toLocaleDateString()}</p>
          <p className="text-slate-400 mb-6">By: {agreement.signer_name}</p>
          <Button onClick={() => navigate(`/contractor/compliance/${agreement.contractor_id}`)} className="bg-emerald-600 hover:bg-emerald-700">
            View Compliance Status
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Sterile Mate" className="h-8" />
            <div>
              <h1 className="text-lg font-bold text-white">Independent Contractor Agreement</h1>
              <p className="text-xs text-slate-400">Digital Signing Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Shield className="w-4 h-4 text-emerald-400" />
            Secure & Legally Binding
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Agreement Info */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm text-slate-400 mb-2">COMPANY</h3>
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="text-white font-semibold">{template?.company_name}</p>
                  <p className="text-sm text-slate-400">{template?.company_address}</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm text-slate-400 mb-2">CONTRACTOR</h3>
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-white font-semibold">{agreement?.contractor_company}</p>
                  <p className="text-sm text-slate-400">{agreement?.contractor_name}</p>
                  <p className="text-sm text-slate-400">{agreement?.contractor_address}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Calendar className="w-4 h-4" />
              Effective Date: {new Date(agreement?.effective_date).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Agreement Sections */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl mb-8">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              Agreement Terms
            </h2>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={expandAllSections} className="text-xs text-slate-400">
                Expand All
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAllSections} className="text-xs text-slate-400">
                Collapse All
              </Button>
            </div>
          </div>
          
          <div className="divide-y divide-slate-700">
            {template?.sections?.map((section) => (
              <div key={section.number} className="overflow-hidden">
                <button
                  onClick={() => toggleSection(section.number)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <span className="text-white font-medium">
                    {section.number}. {section.title}
                  </span>
                  {expandedSections[section.number] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedSections[section.number] && (
                  <div className="px-4 pb-4 bg-slate-900/30">
                    <div className="p-4 bg-slate-900/50 rounded-lg text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">
                      {section.content}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Company Signature (Pre-signed) */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            Company Signature (Pre-signed)
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-400 text-sm">Name</Label>
              <p className="text-white font-semibold">{template?.authorized_signer}</p>
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Title</Label>
              <p className="text-white">{template?.authorized_title}</p>
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Date</Label>
              <p className="text-white">{new Date(agreement?.company_signature?.signed_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Contractor Signature Section */}
        <div className="bg-slate-800/50 border border-amber-500/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Pen className="w-5 h-5 text-amber-400" />
            Your Signature
          </h3>

          {/* Signature Type Toggle */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setSignatureType("typed")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                signatureType === "typed"
                  ? "bg-amber-500 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
            >
              Type Signature
            </button>
            <button
              onClick={() => setSignatureType("drawn")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                signatureType === "drawn"
                  ? "bg-amber-500 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
            >
              Draw Signature
            </button>
          </div>

          {signatureType === "typed" ? (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Full Legal Name *</Label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Type your full legal name"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              {typedSignature && (
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-3xl text-slate-900" style={{ fontFamily: "cursive" }}>
                    {typedSignature}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <Label className="text-slate-300">Draw your signature below</Label>
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={150}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="w-full bg-white rounded-lg cursor-crosshair"
                  style={{ touchAction: "none" }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCanvas}
                  className="absolute top-2 right-2 text-slate-600 hover:text-slate-900"
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label className="text-slate-300">Title (Optional)</Label>
            <Input
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              placeholder="e.g., Owner, Manager, etc."
              className="bg-slate-900 border-slate-600 text-white mt-1"
            />
          </div>
        </div>

        {/* Terms Acceptance */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-terms"
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
              className="mt-1"
            />
            <label htmlFor="accept-terms" className="text-slate-300 text-sm leading-relaxed cursor-pointer">
              I have read and understood all terms of this Independent Contractor / Supplier Agreement. 
              By signing below, I acknowledge that this constitutes a legally binding agreement between 
              my company and Sterile Mate LLC. I understand that I must upload all required compliance 
              documents to the AskIsaac platform before commencing any work.
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/contractor/dashboard")}
            className="border-slate-600 text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !acceptedTerms}
            className="bg-emerald-600 hover:bg-emerald-700 px-8"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                <FileCheck className="w-4 h-4 mr-2" />
                Sign Agreement
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
